import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { CogIcon, LeaveIcon, LightBulbIcon } from '@navikt/aksel-icons';
import { Tooltip } from '@digdir/designsystemet-react';
import cn from 'classnames';

import { useGetUserinfo } from '../../hooks/queries/useGetUserinfo.ts';
import { useLogoutMutation } from '../../hooks/mutations/useLogoutMutation';
import { useSidebarRoutes } from '../../hooks/useSidebarRoutes';
import { useUpdateUserOnboardingMutation, useUserOnboardingQuery } from '../../hooks/queries/onboarding/useUserOnboarding';
import { SidebarItem } from '../SidebarItem';
import itemClasses from '../SidebarItem/SidebarItem.module.css';
import classes from './Sidebar.module.css';

type AccountMenuProps = {
  onNavigate?: () => void;
  collapsed?: boolean;
};

const getInitials = (name: string) =>
  name
    .split(/[\s._@-]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]!.toUpperCase())
    .join('');

/**
 * Innlogget bruker, kontolenker og utlogging. Utlogging er en handling og derfor en knapp.
 */
export const AccountMenu = ({ onNavigate, collapsed = false }: AccountMenuProps) => {
  const { t } = useTranslation();
  const { accountRoutes } = useSidebarRoutes();
  const { data: userInfo } = useGetUserinfo();
  const { mutate: logout, isPending } = useLogoutMutation();
  const headingId = useId();
  const name = userInfo?.nickname || userInfo?.email || '';
  const logoutLabel = t('sidebar.routes.logout');
  const { data: onboarding } = useUserOnboardingQuery(Boolean(userInfo));
  const saveOnboarding = useUpdateUserOnboardingMutation();
  // Hidden tips and «Kom i gang» come back from here, as long as there is something left to do
  const mayShowAgain = Boolean(onboarding?.gettingStarted && (onboarding.checklistHidden || onboarding.tipsClosed));
  const showAgainLabel = t('gettingStarted.nav.showAgain');
  const showAgainButton = (
    <button
      type="button"
      className={cn('ds-focus', itemClasses.item, itemClasses.small, {
        [itemClasses.collapsed]: collapsed,
      })}
      onClick={() => saveOnboarding.mutate({ checklistHidden: false, tipsClosed: false })}
    >
      <span className={itemClasses.icon}>
        <LightBulbIcon aria-hidden />
      </span>
      <span className={cn(itemClasses.label, { 'ds-sr-only': collapsed })}>{showAgainLabel}</span>
    </button>
  );

  const logoutButton = (
    <button
      type="button"
      className={cn('ds-focus', itemClasses.item, itemClasses.small, {
        [itemClasses.collapsed]: collapsed,
      })}
      onClick={() => logout()}
      disabled={isPending}
    >
      <span className={itemClasses.icon}>
        <LeaveIcon aria-hidden />
      </span>
      <span className={cn(itemClasses.label, { 'ds-sr-only': collapsed })}>{logoutLabel}</span>
    </button>
  );

  return (
    <section className={classes.account} aria-labelledby={headingId}>
      <h2 id={headingId} className="ds-sr-only">
        {t('sidebar.accountLabel')}
      </h2>
      {userInfo && (
        <div className={cn(classes.user, { [classes.userCollapsed]: collapsed })}>
          <span aria-hidden="true" className={classes.avatar}>
            {getInitials(name)}
          </span>
          <span className={cn(classes.userText, { 'ds-sr-only': collapsed })}>
            <span className={classes.userName}>{name}</span>
            {userInfo.email && <span className={classes.userEmail}>{userInfo.email}</span>}
          </span>
        </div>
      )}
      <ul className={classes.list} aria-labelledby={headingId}>
        {accountRoutes.map((route) => (
          <SidebarItem
            key={route.route}
            route={route.route}
            icon={<CogIcon aria-hidden />}
            onNavigate={onNavigate}
            size="sm"
            collapsed={collapsed}
          >
            {route.label}
          </SidebarItem>
        ))}
        {mayShowAgain && (
          <li>
            {collapsed ? (
              <Tooltip content={showAgainLabel} placement="right">
                {showAgainButton}
              </Tooltip>
            ) : (
              showAgainButton
            )}
          </li>
        )}
        <li>
          {collapsed ? (
            <Tooltip content={logoutLabel} placement="right">
              {logoutButton}
            </Tooltip>
          ) : (
            logoutButton
          )}
        </li>
      </ul>
    </section>
  );
};
